import { REJECT, array, bool, nullable, num, object, optional, str, type Received } from 'validation';

import { getRecord } from 'api/shared/getRecord';

// Source of truth for the wire shape: nap-backend `routes/workflows.js` GET /workflows/:workflow_id. The node instance
// view shows an operator message's subject, each action's message, and lets the operator pick one of its response
// options. It renders no input fields and no dropdown parameters, so it declares none: a drift in those must not take
// away the buttons it does render. `can_confirm` says whether the caller may answer every open action (nap-backend#2009).
// The respond route checks each action on its own, in the zone of the node instance waiting for it (its `confirm_zone_id`
// / `confirm_zone_name`, nap-backend#1951); each action's own `can_confirm` is that check, so the panel offers the
// buttons per action.
//
// The text columns are nullable in `workflows` / `workflow_actions` and are copied from node configuration.

/** An answer the operator can give: a node definition handle, so `id` is a uuid and the thing `respond` sends back. */
export interface OperatorMessageResponseOption {
  readonly id: string;
  readonly label: string;
}

/** The answer given: the chosen option's label, or the bare label a cancelled node instance writes. */
export interface OperatorMessageResponse {
  readonly label: string;
  readonly user: string | null;
}

export interface OperatorMessageAction {
  readonly workflow_action_id: number;
  readonly subject: string | null;
  readonly message: string | null;
  readonly response: OperatorMessageResponse | null;
  readonly response_options: readonly OperatorMessageResponseOption[];
  // Optional: a backend before nap-backend#1951 sends none, and the message's `can_confirm` stands in.
  readonly confirm_zone_id?: number | null;
  readonly confirm_zone_name?: string | null;
  readonly can_confirm?: boolean;
}

export interface OperatorMessage {
  readonly workflow_subject: string | null;
  // Display only, so optional: compared with each action's confirm zone (nap-backend#2011, #1951).
  readonly zone_id?: number | null;
  readonly zone_name?: string | null;
  readonly can_confirm: boolean;
  readonly workflows_actions: readonly OperatorMessageAction[];
}

const messageSchema = object<OperatorMessage>({
  workflow_subject: nullable(str()),
  zone_id: optional(nullable(num())),
  zone_name: optional(nullable(str())),
  // Deliberately refused when missing or not a boolean: guessing either way is wrong. `true` offers answers the server
  // refuses; `false` greys out a message the operator may well answer. The panel shows the load error instead, so a
  // backend without nap-backend#2009 is noticed rather than silently locking every message.
  can_confirm: bool(REJECT),
  workflows_actions: array(
    object<OperatorMessageAction>({
      workflow_action_id: num(REJECT),
      subject: nullable(str()),
      message: nullable(str()),
      response: nullable(object<OperatorMessageResponse>({ label: str(), user: nullable(str()) })),
      response_options: array(object<OperatorMessageResponseOption>({ id: str(REJECT), label: str() })),
      confirm_zone_id: optional(nullable(num())),
      confirm_zone_name: optional(nullable(str())),
      can_confirm: optional(bool()),
    })
  ),
});

/**
 * GET `/workflows/:workflow_id` for the operator message panel in the node instance view. See `getRecord` for the
 * outcomes: `null` is the server saying there is no such workflow.
 */
export const getOperatorMessage = (id: number): Promise<Received<OperatorMessage | null>> =>
  getRecord(`/workflows/${id}`, { schema: messageSchema, source: 'getOperatorMessage' });
